import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import Sidebar from "../components/Sidebar";
import { toast } from "sonner";
import { Flame, ThermometerHot, Snowflake } from "@phosphor-icons/react";
import { getLeadScoreColor } from "../config/uiHelpers";

const LeadScoring = ({ user, logout }) => {
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchScores = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/crm/lead-scoring`);
      setScores(res.data);
    } catch (err) { toast.error("Failed to load scores"); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchScores(); }, [fetchScores]);

  const autoAssign = async () => {
    try {
      const res = await axios.post(`${API}/crm/leads/auto-assign`);
      toast.success(res.data.message);
    } catch (err) { toast.error("Auto-assign failed"); }
  };

  const getTempIcon = (temp) => {
    if (temp === "hot") return <Flame size={20} weight="fill" className="text-red-500" />;
    if (temp === "warm") return <ThermometerHot size={20} weight="fill" className="text-orange-500" />;
    return <Snowflake size={20} weight="fill" className="text-blue-400" />;
  };

  const getTempBadge = (temp) => {
    if (temp === "hot") return "bg-red-50 text-red-700 border-red-200";
    if (temp === "warm") return "bg-orange-50 text-orange-700 border-orange-200";
    return "bg-blue-50 text-blue-400 border-blue-200";
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <div className="flex-1 ml-64 p-8">
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-4xl sm:text-5xl tracking-tighter leading-none font-bold mb-2" style={{fontFamily: 'Outfit, sans-serif'}}>Lead Scoring</h1>
            <p className="text-base text-slate-700">AI-powered lead scoring and auto-assignment</p>
          </div>
          <button data-testid="auto-assign-btn" onClick={autoAssign}
            className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-6 py-3 font-medium transition-colors duration-300">
            Auto-Assign Leads
          </button>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-3 gap-6 mb-8">
          {["hot", "warm", "cold"].map(temp => {
            const count = scores.filter(s => s.temperature === temp).length;
            return (
              <div key={temp} className="bg-white border border-slate-200 rounded-none shadow-sm p-6 flex items-center gap-4">
                {getTempIcon(temp)}
                <div>
                  <div className="text-3xl font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>{count}</div>
                  <div className="text-sm text-slate-600 capitalize">{temp} Leads</div>
                </div>
              </div>
            );
          })}
        </div>

        {loading ? <div className="flex justify-center py-20"><div className="loading-spinner"></div></div> : (
          <div data-testid="lead-scores-table" className="bg-white border border-slate-200 rounded-none shadow-sm overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-y border-slate-200">
                  <th className="text-left p-4 text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Contact</th>
                  <th className="text-left p-4 text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Company</th>
                  <th className="text-left p-4 text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Score</th>
                  <th className="text-left p-4 text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Temperature</th>
                  <th className="text-left p-4 text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Activities</th>
                  <th className="text-left p-4 text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Deals</th>
                  <th className="text-left p-4 text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Tags</th>
                </tr>
              </thead>
              <tbody>
                {scores.map((lead) => (
                  <tr key={lead.contact_id} className="border-b border-slate-100 hover:bg-slate-50/50">
                    <td className="p-4">
                      <p className="font-medium">{lead.name}</p>
                      <p className="text-xs text-slate-500">{lead.email}</p>
                    </td>
                    <td className="p-4 text-sm text-slate-600">{lead.company || '-'}</td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <div className="w-16 bg-slate-200 h-2 rounded-none">
                          <div className="h-2" style={{width: `${lead.score}%`, backgroundColor: getLeadScoreColor(lead.temperature)}}></div>
                        </div>
                        <span className="text-sm font-bold">{lead.score}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className={`rounded-none px-2 py-1 text-xs font-bold uppercase tracking-wider border ${getTempBadge(lead.temperature)}`}>
                        {lead.temperature}
                      </span>
                    </td>
                    <td className="p-4 text-sm">{lead.activity_count}</td>
                    <td className="p-4 text-sm">{lead.deal_count}</td>
                    <td className="p-4">
                      <div className="flex gap-1 flex-wrap">
                        {lead.tags?.map(tag => <span key={tag} className="rounded-none px-1.5 py-0.5 text-xs bg-slate-100 text-slate-600 border border-slate-200">{tag}</span>)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadScoring;
